import { useEffect, useState } from "react";
import { decideCliLogin, cliLoginPending, signInUrl } from "../api.js";
import type { Session } from "../app.js";

type State = "checking" | "ready" | "approved" | "denied" | "gone";

/** Where `saucer login` sends the browser: confirm the code, hand over a token. */
export function CliApproval({ session }: { session: Session }) {
  const [code, setCode] = useState(
    () => new URLSearchParams(window.location.search).get("code") ?? "",
  );
  const [state, setState] = useState<State>("checking");

  useEffect(() => {
    if (!session || !code) {
      setState(code ? "checking" : "ready");
      return;
    }
    void cliLoginPending(code).then((response) => setState(response.ok ? "ready" : "gone"));
  }, [session, code]);

  if (!session) {
    return (
      <main className="narrow stack">
        <h1>Connect the CLI</h1>
        <p className="meta">Sign in first, then this page will ask you to confirm the code.</p>
        <a className="button button--primary" href={signInUrl("", `/cli?code=${code}`)}>
          Continue with GitHub
        </a>
      </main>
    );
  }

  if (state === "approved") {
    return (
      <main className="narrow stack">
        <h1>The CLI is connected</h1>
        <p className="meta">Back to your terminal — `saucer` is signed in as {session.user.username}.</p>
      </main>
    );
  }

  if (state === "denied" || state === "gone") {
    return (
      <main className="narrow stack">
        <h1>{state === "denied" ? "Request refused" : "That code expired"}</h1>
        <p className="meta">Run `saucer login` again to start a new request.</p>
      </main>
    );
  }

  return (
    <main className="narrow stack">
      <h1>Connect the CLI</h1>
      <p className="meta">
        Check that this matches the code in your terminal, then approve it. The CLI gets a token
        that can upload and delete files as {session.user.username}.
      </p>

      <input
        className="input mono code-input"
        value={code}
        onChange={(event) => setCode(event.target.value)}
        placeholder="xxxx-xxxx"
        autoComplete="off"
        spellCheck={false}
      />

      <div className="row">
        <button
          className="button button--primary"
          type="button"
          disabled={!code}
          onClick={async () => {
            const response = await decideCliLogin(code, true);
            setState(response.ok ? "approved" : "gone");
          }}
        >
          Approve
        </button>
        <button
          className="button"
          type="button"
          disabled={!code}
          onClick={async () => {
            await decideCliLogin(code, false);
            setState("denied");
          }}
        >
          Refuse
        </button>
      </div>
    </main>
  );
}
